import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Settings, Loader2, X, Check } from 'lucide-react';

import ConnectionStatus from './ConnectionStatus';
import { saveAgentSettings } from '../../../redux/DigestSlice/digestSlice';
import './digest-design.css';

/**
 * Daily Digest · Agent settings modal.
 *
 * ON THE DESIGN SOURCE. The canvas registers an artboard
 * `digest-settings → window.DailyDigestSettingsScreen`, but no file in the
 * project defines that component (checked screens/daily-digest.jsx,
 * screens/account-extras.jsx and screens/people-pipeline.jsx). So this is built
 * from the project's OWN modal vocabulary — the Work-experience / Education
 * DetailModal in screens/people-pipeline.jsx — plus the settings the API
 * actually exposes, rather than invented from nothing. If the artboard is
 * authored later, this should be re-read against it.
 *
 * Bounds mirror `SETTING_BOUNDS` in controllers/webapp/agent.controller.js,
 * which is the authority; these only stop a value that is guaranteed to 400.
 */

const BOUNDS = {
    dailyCap: [0, 100],
    linkedinCap: [0, 5],
    digestSize: [1, 100],
};
const CONTACTS_PER_FETCH_OPTIONS = [10, 20, 30, 40, 50];

// The digest goes out at one fixed time for everyone (server DIGEST_SEND_HOUR /
// DIGEST_SEND_TIMEZONE). Written out rather than derived from the viewer's
// clock — the whole point is that it is the same moment for every user.
const DIGEST_SEND_LABEL = '8:00 AM Mountain Time';

/**
 * MUST track the server defaults in models/Users.js (agentSettings).
 * A user document created before this feature has no agentSettings at all and
 * the API returns `{}` — these fallbacks are what the form then renders, and
 * what a Save without touching anything persists. A mismatch here silently
 * changes someone's real send volume and credit spend.
 */
const DEFAULTS = {
    enabled: false,
    dailyCap: 5,
    linkedinCap: 1,
    fetchCap: 2,
    digestSize: 100,
    defaultContactsPerFetch: 10,
};

const clamp = (v, [min, max]) => Math.min(max, Math.max(min, v));

const NumberField = ({ label, hint, bounds, value, onChange }) => {
    // Explicitly associated: without htmlFor/id the visible label is decoration
    // to a screen reader, and clicking it does not focus the field.
    const id = useId();
    const hintId = `${id}-hint`;
    return (
        <div className="cc-field">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <label htmlFor={id} style={{ fontSize: 13, fontWeight: 700, color: 'var(--cc-ink-900)' }}>{label}</label>
                <span className="cc-chip cc-chip-neutral" style={{ fontSize: 10 }}>{bounds[0]}–{bounds[1]}</span>
            </div>
            <input
                id={id}
                aria-describedby={hintId}
                className="cc-input"
                type="number"
                min={bounds[0]}
                max={bounds[1]}
                value={value}
                onChange={(e) => onChange(clamp(Number(e.target.value) || 0, bounds))}
            />
            <p id={hintId} className="cc-tiny" style={{ marginTop: 8, lineHeight: 1.5 }}>{hint}</p>
        </div>
    );
};

const AgentSettingsModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { settings, creditsBalance, channels, error } = useSelector((state) => state.digest);

    const [form, setForm] = useState(DEFAULTS);
    const [saving, setSaving] = useState(false);
    const closeRef = useRef(null);

    // Re-seed from the server whenever the modal opens, so an abandoned edit
    // never persists into the next open as if it had been saved.
    useEffect(() => {
        if (!open) return;
        setForm({
            enabled: !!settings?.enabled,
            dailyCap: settings?.dailyCap ?? DEFAULTS.dailyCap,
            linkedinCap: settings?.linkedinCap ?? DEFAULTS.linkedinCap,
            fetchCap: settings?.fetchCap ?? DEFAULTS.fetchCap,
            digestSize: settings?.digestSize ?? DEFAULTS.digestSize,
            defaultContactsPerFetch: settings?.defaultContactsPerFetch ?? DEFAULTS.defaultContactsPerFetch,
        });
    }, [open, settings]);

    // Esc closes; focus moves into the dialog; the page behind stops scrolling.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    // What approving a whole digest would cost — one credit per contact.
    const maxDailySpend = useMemo(
        () => (Number(form.digestSize) || 0) * (Number(form.defaultContactsPerFetch) || 0),
        [form.digestSize, form.defaultContactsPerFetch]
    );
    const overBudget = creditsBalance != null && maxDailySpend > creditsBalance;

    const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await dispatch(saveAgentSettings({
                // A real JSON boolean: the API strictly rejects anything else,
                // and this is the flag that turns on unattended outreach.
                enabled: !!form.enabled,
                dailyCap: clamp(Number(form.dailyCap), BOUNDS.dailyCap),
                linkedinCap: clamp(Number(form.linkedinCap), BOUNDS.linkedinCap),
                // Not a separate control any more: every opportunity the agent
                // proposes may be a fetch row, so the row cap tracks the
                // opportunity count rather than drifting behind it.
                fetchCap: clamp(Number(form.digestSize), BOUNDS.digestSize),
                digestSize: clamp(Number(form.digestSize), BOUNDS.digestSize),
                defaultContactsPerFetch: CONTACTS_PER_FETCH_OPTIONS.includes(Number(form.defaultContactsPerFetch))
                    ? Number(form.defaultContactsPerFetch)
                    : DEFAULTS.defaultContactsPerFetch,
                // The HOUR is fixed for everyone, but the user's zone is what
                // makes their local calendar day correct — and one-digest-per-day
                // is keyed on that day.
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })).unwrap();
            onClose();
        } catch (_err) {
            // saveAgentSettings already fires the error toast; the catch exists
            // so a rejected save leaves the modal OPEN with the user's edits
            // intact rather than closing as though it had worked.
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div
            className="cc-modal-scrim"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="cc-scope cc-modal-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Agent settings"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="cc-modal-head">
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--cc-primary-50)', color: 'var(--cc-primary)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        <Settings style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="cc-grow">
                        <div className="cc-eyebrow">Daily Digest</div>
                        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>Agent settings</div>
                        <div className="cc-tiny">
                            {form.enabled ? `Runs every day at ${DIGEST_SEND_LABEL}` : 'Not running on a schedule'}
                        </div>
                    </div>
                    <button ref={closeRef} type="button" className="cc-icon-btn" onClick={onClose} aria-label="Close settings">
                        <X style={{ width: 14, height: 14 }} />
                    </button>
                </div>

                <div className="cc-modal-body">
                    {error && (
                        <div className="cc-tiny" style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '9px 12px' }}>
                            {typeof error === 'string' ? error : 'Something went wrong loading or saving settings.'}
                        </div>
                    )}

                    {/* The switch that decides whether anything below ever runs,
                        so it leads rather than sitting among the numbers. */}
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
                        padding: '14px 16px', borderRadius: 12,
                        background: form.enabled ? 'var(--cc-primary-50)' : '#FAFBFD',
                        border: `1px solid ${form.enabled ? 'var(--cc-primary-100)' : 'var(--cc-divider)'}`,
                    }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>Let the AI prepare outreach for me each morning</div>
                            <div className="cc-tiny" style={{ marginTop: 3, lineHeight: 1.5 }}>
                                {form.enabled
                                    ? `Your digest is prepared every day at ${DIGEST_SEND_LABEL}.`
                                    : `When on, your digest is prepared every day at ${DIGEST_SEND_LABEL}.`}
                                {' '}You can also generate one by hand any time.
                            </div>
                        </div>
                        <button
                            type="button"
                            className="cc-switch"
                            role="switch"
                            aria-checked={form.enabled}
                            aria-label="Prepare outreach each morning"
                            onClick={() => set('enabled', !form.enabled)}
                        >
                            <span />
                        </button>
                    </div>

                    {channels && (
                        <div>
                            <div className="cc-eyebrow" style={{ marginBottom: 6 }}>What the agent may send as</div>
                            <div style={{ border: '1px solid var(--cc-border)', borderRadius: 12, padding: '2px 14px' }}>
                                <ConnectionStatus channels={channels} />
                            </div>
                        </div>
                    )}

                    {/* Sending and finding are separated deliberately: they are
                        different budgets, and conflating them is what made the
                        old panel's two "contact" tiles unreadable. */}
                    <div>
                        <div className="cc-eyebrow" style={{ marginBottom: 8 }}>Each day, send at most</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                            <NumberField
                                label="Daily email cap"
                                hint="Protects your email reputation."
                                bounds={BOUNDS.dailyCap}
                                value={form.dailyCap}
                                onChange={(v) => set('dailyCap', v)}
                            />
                            <NumberField
                                label="Daily LinkedIn cap"
                                hint="LinkedIn restricts automated-looking activity — hence the default of 1."
                                bounds={BOUNDS.linkedinCap}
                                value={form.linkedinCap}
                                onChange={(v) => set('linkedinCap', v)}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="cc-eyebrow" style={{ marginBottom: 8 }}>Each day, find</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                            <NumberField
                                label="Opportunities per day"
                                hint="Jobs and hiring signals matched to your profile."
                                bounds={BOUNDS.digestSize}
                                value={form.digestSize}
                                onChange={(v) => set('digestSize', clamp(v || BOUNDS.digestSize[0], BOUNDS.digestSize))}
                            />
                            <div className="cc-field" style={{ borderColor: 'var(--cc-primary-100)', background: 'rgba(238,241,254,.35)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--cc-ink-900)' }}>Contacts per opportunity</label>
                                    <span className="cc-chip cc-chip-primary" style={{ fontSize: 10 }}>default</span>
                                </div>
                                <div className="cc-count-grid">
                                    {CONTACTS_PER_FETCH_OPTIONS.map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            className={Number(form.defaultContactsPerFetch) === n ? 'is-active' : ''}
                                            onClick={() => set('defaultContactsPerFetch', n)}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                                <p className="cc-tiny" style={{ marginTop: 8, lineHeight: 1.5 }}>
                                    Used on a fetch-contact row until you pick a different count for it. One credit per contact.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* The ceiling, stated before Save rather than discovered after.
                        rows × contacts, not rows: pricing this off the row cap
                        understated the real spend by the contact count. */}
                    <div className="cc-tiny" style={{ lineHeight: 1.6, borderTop: '1px solid var(--cc-divider)', paddingTop: 14 }}>
                        Proposes up to <strong style={{ color: 'var(--cc-ink-700)' }}>{form.digestSize}</strong> a day.
                        Approving every one at <strong style={{ color: 'var(--cc-ink-700)' }}>{form.defaultContactsPerFetch}</strong> contacts
                        would spend <strong style={{ color: overBudget ? '#B91C1C' : 'var(--cc-primary)' }}>{maxDailySpend.toLocaleString()}</strong> credits
                        {creditsBalance != null && <> of your <strong style={{ color: 'var(--cc-ink-700)' }}>{creditsBalance.toLocaleString()}</strong></>}.
                        {overBudget && (
                            <span style={{ color: '#B91C1C' }}> You’d run out partway — the agent stops proposing once your balance can’t cover the next one.</span>
                        )}
                    </div>
                </div>

                <div className="cc-modal-foot">
                    <span className="cc-tiny">Changes apply to tomorrow’s digest.</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" className="cc-btn cc-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="button" className="cc-btn cc-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Check style={{ width: 13, height: 13 }} />}
                        Save settings
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AgentSettingsModal;
